import { useIntl } from "@leagueplatform/locales";
import {
  Box,
  ParagraphText,
  HeadingText,
  Checkbox,
} from "@leagueplatform/genesis-core";
import type { LocalesKey } from "types/types";

const checkboxSectionCss = {
  ".GDS-checkbox-input": {
    border: "solid 1px $interactiveBorderDefault",
    "&:checked": { border: "$none" },
  },
  ".GDS-checkbox-label": {
    marginBlockEnd: "$one",
  },
  ".GDS-checkbox-hint": {
    marginInlineStart: "$none",
  },
} as const;

const checkboxSectionCssTight = {
  ...checkboxSectionCss,
  ".GDS-checkbox-label": {
    marginBlockEnd: "$half",
  },
} as const;

const footnoteTextCss = {
  color: "$onSurfaceTextSubdued",
  fontStyle: "regular",
} as const;

const subsectionHeadingCss = {
  marginBlockEnd: "$half",
  fontWeight: "$subtitleOne",
} as const;

export interface PreferenceCheckboxSectionProps {
  id: string;
  name: string;
  title: string;
  label: string;
  hintMessageId?: LocalesKey;
  hint?: string;
  hintId?: string;
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  labelMarginBlockEnd?: "$one" | "$half";
}

export const PreferenceCheckboxSection = ({
  id,
  name,
  title,
  label,
  hintMessageId,
  hint,
  hintId,
  checked,
  onChange,
  disabled,
  loading,
  labelMarginBlockEnd = "$one",
}: PreferenceCheckboxSectionProps) => {
  const { formatMessage } = useIntl();
  const checkboxCss =
    labelMarginBlockEnd === "$half"
      ? checkboxSectionCssTight
      : checkboxSectionCss;

  const resolvedHint =
    hint || (hintMessageId ? formatMessage({ id: hintMessageId }) : undefined);

  return (
    <Box>
      <HeadingText size="xs" level="3" css={subsectionHeadingCss}>
        {title}
      </HeadingText>
      <Checkbox
        id={id}
        name={name}
        css={checkboxCss}
        label={<ParagraphText size="xs">{label}</ParagraphText>}
        checked={checked}
        onChange={onChange}
        layout="inline"
        hint={
          resolvedHint ? (
            <ParagraphText
              id={hintId}
              as="span"
              size="xs"
              css={footnoteTextCss}
            >
              {resolvedHint}
            </ParagraphText>
          ) : undefined
        }
        loading={loading}
        disabled={disabled}
      />
    </Box>
  );
};
